import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loginUser, sendPhoneCode, registerAccount, sendPasswordResetCode, confirmPasswordReset } from "./loginApi";
import {
  selectAccId,
  selectFromClient,
  selectIsLogOut,
  selectUsers,
  setAccId,
  setCurrentUser,
  setFromClient,
  setName,
  setUserId,
  setUsers,
} from "./loginSlice";
import { useNavigate } from "react-router-dom";
import "./login.css";
import { rBlueIcon, rIcon } from "../../static/svgs";
import { useSocket } from "../chat/useSocket";
import { connectionFailedMessage } from "../../utils/fetchWithRetry";

const PASSWORD_HINT =
  "Не менее 6 символов, заглавная буква и спецсимвол ($ % & # …)";

function getPasswordStrengthError(password) {
  const pwd = String(password || "");
  const missing = [];
  if (pwd.length < 6) missing.push("длину от 6 символов");
  if (!/[A-ZА-ЯЁ]/.test(pwd)) missing.push("заглавную букву");
  if (!/[!@#$%^&*()_+\-=[\]{}|;:'",.<>/?`~\\]/.test(pwd)) {
    missing.push("спецсимвол ($ % & # …)");
  }
  if (!missing.length) return null;
  return `Пароль слишком простой. Нужны: ${missing.join(", ")}`;
}

const Login = () => {
  const { socket } = useSocket();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const users = useSelector(selectUsers);
  const isLogout = useSelector(selectIsLogOut);
  const from_client = useSelector(selectFromClient);
  
  const [showRegistration, setShowRegistration] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [passwordResetCodeSent, setPasswordResetCodeSent] = useState(false);
  const [resetPhone, setResetPhone] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [registrationPhone, setRegistrationPhone] = useState("");
  const [registrationCode, setRegistrationCode] = useState("");
  const [registrationName, setRegistrationName] = useState("");
  const [registrationPassword, setRegistrationPassword] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("");
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [showAgreementModal, setShowAgreementModal] = useState(false);

  const buildStoredUser = (phone, pass, data) => ({
    username: phone,
    password: pass,
    company_name: data.name,
    user_id: data.id,
    account_type: data.account_type || "business",
    public_id: data.public_id || null,
    photo_path: data.photo_path || null,
    accountant_id: data.accountant_id || null,
  });

  const applyAuthSession = (phone, pass, data) => {
    dispatch(setName(data.name));
    dispatch(setUserId(data.id));
    dispatch(setAccId(data.accountant_id || null));
    dispatch(setCurrentUser(data));
    const newUser = buildStoredUser(phone, pass, data);
    const existingUserIndex = users.findIndex((user) => user.username === newUser.username);
    const updatedUsers =
      existingUserIndex === -1
        ? [...users, newUser]
        : users.map((u, i) => (i === existingUserIndex ? newUser : u));
    localStorage.setItem("userList", JSON.stringify(updatedUsers));
    localStorage.setItem("user_id", data.id);
    dispatch(setUsers(updatedUsers));
  };

  // Функция для нормализации номера телефона
  const normalizePhone = (value) => {
    // Удаляем все нецифровые символы
    let digits = value.replace(/\D/g, '');
    
    // Если номер начинается с 8, заменяем на 7
    if (digits.length > 0 && digits[0] === '8') {
      digits = '7' + digits.substring(1);
    }
    
    // Ограничиваем длину до 11 символов (формат 79999999999)
    if (digits.length > 11) {
      digits = digits.substring(0, 11);
    }
    
    return digits;
  };

  const handleLogin = () => {
    setIsLoading(true);
    setConnectionStatus("");
    loginUser(username, password, {
      onRetryProgress: ({ message, status }) => {
        if (message && status !== "completed") {
          setConnectionStatus(message);
        }
      },
    })
      .then((data) => {
        setConnectionStatus("");
        if (data?.error || data?.id == null) {
          alert(data?.error || "Неверные данные");
        } else {
          console.log("handleLogin", data);
          applyAuthSession(username, password, data);
          navigate("/client");
        }
      })
      .catch((error) => {
        console.error("Ошибка при входе:", error);
        setConnectionStatus(error.userMessage || connectionFailedMessage());
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleBackClient = () => {
    dispatch(setFromClient(false));
    navigate("/client");
  };

  const handleSendCode = async () => {
    if (!registrationPhone) {
      alert("Введите номер телефона");
      return;
    }
    if (!agreementAccepted) {
      alert("Необходимо согласиться с пользовательским соглашением");
      return;
    }
    setIsLoading(true);
    try {
      const result = await sendPhoneCode(registrationPhone);
      if (result.success) {
        setCodeSent(true);
      } else {
        alert(result.error || "Ошибка при отправке кода");
      }
    } catch (error) {
      console.error("Ошибка при отправке кода:", error);
      alert("Ошибка при отправке кода");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!registrationCode || !registrationName || !registrationPassword) {
      alert("Заполните все поля");
      return;
    }
    const passwordError = getPasswordStrengthError(registrationPassword);
    if (passwordError) {
      alert(passwordError);
      return;
    }
    setIsLoading(true);
    try {
      const result = await registerAccount(
        registrationPhone,
        registrationCode,
        registrationName,
        registrationPassword
      );
      if (result.success && result.user) {
        applyAuthSession(registrationPhone, registrationPassword, result.user);
        navigate("/client");
      } else {
        alert(result.error || "Неверный код");
      }
    } catch (error) {
      console.error("Ошибка при проверке кода:", error);
      alert("Ошибка при проверке кода");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegistrationClick = () => {
    setShowRegistration(true);
    setShowPasswordReset(false);
    setCodeSent(false);
    setRegistrationPhone("");
    setRegistrationCode("");
    setRegistrationName("");
    setRegistrationPassword("");
    setAgreementAccepted(false);
  };

  const handleForgotPasswordClick = () => {
    setShowPasswordReset(true);
    setShowRegistration(false);
    setPasswordResetCodeSent(false);
    setResetPhone(username || "");
    setResetCode("");
    setResetPassword("");
  };

  const handleCancelPasswordReset = () => {
    setShowPasswordReset(false);
    setPasswordResetCodeSent(false);
    setResetPhone("");
    setResetCode("");
    setResetPassword("");
  };

  const handleSendPasswordResetCode = async () => {
    if (!resetPhone) {
      alert("Введите номер телефона");
      return;
    }
    setIsLoading(true);
    try {
      const result = await sendPasswordResetCode(resetPhone);
      if (result.success) {
        setPasswordResetCodeSent(true);
      } else {
        alert(result.error || "Ошибка при отправке кода");
      }
    } catch (error) {
      console.error("Ошибка при отправке кода сброса пароля:", error);
      alert("Ошибка при отправке кода");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmPasswordReset = async () => {
    if (!resetCode || !resetPassword) {
      alert("Введите код из SMS и новый пароль");
      return;
    }
    const passwordError = getPasswordStrengthError(resetPassword);
    if (passwordError) {
      alert(passwordError);
      return;
    }
    setIsLoading(true);
    try {
      const result = await confirmPasswordReset(resetPhone, resetCode, resetPassword);
      if (result.success) {
        setUsername(resetPhone);
        setPassword(resetPassword);
        handleCancelPasswordReset();
        alert("Пароль обновлён. Можно войти.");
      } else {
        alert(result.error || "Не удалось сменить пароль");
      }
    } catch (error) {
      console.error("Ошибка подтверждения сброса пароля:", error);
      alert("Ошибка сброса пароля");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelRegistration = () => {
    setShowRegistration(false);
    setCodeSent(false);
    setRegistrationPhone("");
    setRegistrationCode("");
    setRegistrationName("");
    setRegistrationPassword("");
    setAgreementAccepted(false);
  };

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const userData = localStorage.getItem("userList");
        if (userData) {
          const parsedUserData = JSON.parse(userData);
          dispatch(setUsers(parsedUserData));
          if (parsedUserData.length > 0) {
            const lastUser = parsedUserData[parsedUserData.length - 1];
            loginUser(lastUser.username, lastUser.password, {
              onRetryProgress: ({ message, status }) => {
                if (message && status !== "completed") {
                  console.log("[auto-login]", message);
                }
              },
            })
              .then((data) => {
                if (data?.error || data?.id == null) {
                  console.warn("[auto-login] пропуск:", data?.error || "нет id");
                  return;
                }
                const newUser = {
                  username: lastUser.username,
                  password: lastUser.password,
                  company_name: data.name,
                  user_id: data.id,
                  account_type: data.account_type || lastUser.account_type || "business",
                  public_id: data.public_id || lastUser.public_id || null,
                  photo_path: data.photo_path || lastUser.photo_path || null,
                  accountant_id: data.accountant_id || null,
                };
                const updatedUsers = [...parsedUserData];
                updatedUsers[parsedUserData.length - 1] = newUser;
                localStorage.setItem(
                  "userList",
                  JSON.stringify(updatedUsers)
                );

                try {
                  const userId = localStorage.getItem("user_id");
                  console.log("userId", userId);
                  if (!userId) {
                      console.log('register from login')
                      socket.emit("register", {
                          user_id: userId,
                          is_buh: false
                      });
                  }
                } catch (err) {
                  console.warn(
                    "Ошибка получения user_id из localStorage",
                    err
                  );
                }

                localStorage.setItem("user_id", data.id);
                console.log("localstorage", data);
                dispatch(setUsers(updatedUsers));
                dispatch(setName(data.name));
                dispatch(setUserId(data.id));
                dispatch(setAccId(data.accountant_id));
                dispatch(setCurrentUser(data));
                navigate("/client");
              })
              .catch((error) => {
                console.error("Ошибка при автоматической авторизации:", error);
              });
          }
        }
      } catch (error) {
        console.error("Failed to load user data:", error);
      }
    };

    if (!isLogout) loadUserData();
  }, []);

  return (
    <div
      style={{
        width: "100%",
        height: "100vh",
        display: "flex",
        // gap: '30px',
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        backgroundColor: "#3262FF",
      }}
    >
      <div className="login_menu_cntr"></div>

      <div className="top_login_container">
        <label className="manrope-bold color_white">Вход</label>
        <div className="ricon_cntr1">{rIcon}</div>
      </div>
      {/* <div className='title_cntr'>
                {rBlueIcon}
                <label style={{ marginLeft: '-8px' }} className='philosopher-bold-italic color_white color_blue'>егистратор аутсорсинг</label>
            </div> */}
      <div className="cntr_login">
        {!showRegistration && !showPasswordReset ? (
          <>
            <label
              style={{ textAlign: "left", width: "100%" }}
              className="color_dark manrope-bold"
            >
              Логин
            </label>
            <input
              className="input_atrib manrope-bold"
              type="text"
              placeholder="Номер телефона (без +)"
              value={username}
              onChange={(e) => setUsername(normalizePhone(e.target.value))}
            />
            <label
              style={{ textAlign: "left", width: "100%" }}
              className="color_dark manrope-bold"
            >
              Пароль
            </label>
            <input
              className="input_atrib manrope-bold"
              type="password"
              placeholder="Пароль"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              className="btn_login manrope-bold"
              onClick={handleLogin}
              disabled={isLoading}
            >
              {isLoading ? "Подключение…" : "Войти"}
            </button>
            {connectionStatus ? (
              <div
                style={{
                  marginTop: "10px",
                  padding: "8px 10px",
                  borderRadius: "8px",
                  backgroundColor: "#fff3cd",
                  color: "#664d03",
                  fontSize: "13px",
                  lineHeight: 1.4,
                  textAlign: "center",
                }}
                className="manrope-bold"
              >
                {connectionStatus}
              </div>
            ) : null}
            <div
              style={{
                textAlign: "center",
                marginTop: "10px",
                cursor: "pointer",
                color: "#3262FF",
                fontSize: "14px",
              }}
              className="manrope-bold"
              onClick={handleForgotPasswordClick}
            >
              Забыли пароль?
            </div>
            <div
              style={{
                textAlign: "center",
                marginTop: "10px",
                cursor: "pointer",
                color: "#3262FF",
                fontSize: "14px",
              }}
              className="manrope-bold"
              onClick={handleRegistrationClick}
            >
              Регистрация
            </div>
          </>
        ) : showPasswordReset ? (
          <>
            {!passwordResetCodeSent ? (
              <>
                <label
                  style={{ textAlign: "left", width: "100%" }}
                  className="color_dark manrope-bold"
                >
                  Номер телефона
                </label>
                <input
                  className="input_atrib manrope-bold"
                  type="tel"
                  placeholder="Номер телефона (без +)"
                  value={resetPhone}
                  onChange={(e) => setResetPhone(normalizePhone(e.target.value))}
                  disabled={isLoading}
                />
                <button
                  className="btn_login manrope-bold"
                  onClick={handleSendPasswordResetCode}
                  disabled={isLoading}
                  style={{
                    backgroundColor: isLoading ? "#94A3B8" : "#3262FF",
                    cursor: isLoading ? "not-allowed" : "pointer",
                  }}
                >
                  {isLoading ? "Отправка..." : "Отправить SMS-код"}
                </button>
                <div
                  style={{
                    textAlign: "center",
                    marginTop: "10px",
                    cursor: "pointer",
                    color: "#94A3B8",
                    fontSize: "14px",
                  }}
                  className="manrope-bold"
                  onClick={handleCancelPasswordReset}
                >
                  Отмена
                </div>
              </>
            ) : (
              <>
                <label
                  style={{ textAlign: "left", width: "100%" }}
                  className="color_dark manrope-bold"
                >
                  Код из SMS
                </label>
                <input
                  className="input_atrib manrope-bold"
                  type="text"
                  placeholder="Введите код"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  disabled={isLoading}
                  maxLength={4}
                />
                <label
                  style={{ textAlign: "left", width: "100%" }}
                  className="color_dark manrope-bold"
                >
                  Новый пароль
                </label>
                <input
                  className="input_atrib manrope-bold"
                  type="password"
                  placeholder="Новый пароль"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  disabled={isLoading}
                />
                <div
                  style={{
                    textAlign: "left",
                    width: "100%",
                    fontSize: "12px",
                    color: "#94A3B8",
                    marginTop: "-4px",
                    marginBottom: "8px",
                  }}
                  className="manrope-bold"
                >
                  {PASSWORD_HINT}
                </div>
                <button
                  className="btn_login manrope-bold"
                  onClick={handleConfirmPasswordReset}
                  disabled={isLoading}
                  style={{
                    backgroundColor: isLoading ? "#94A3B8" : "#3262FF",
                    cursor: isLoading ? "not-allowed" : "pointer",
                  }}
                >
                  {isLoading ? "Сохранение..." : "Сохранить пароль"}
                </button>
                <div
                  style={{
                    textAlign: "center",
                    marginTop: "10px",
                    cursor: "pointer",
                    color: "#94A3B8",
                    fontSize: "14px",
                  }}
                  className="manrope-bold"
                  onClick={handleCancelPasswordReset}
                >
                  Отмена
                </div>
              </>
            )}
          </>
        ) : (
          <>
            {!codeSent ? (
              <>
                <label
                  style={{ textAlign: "left", width: "100%" }}
                  className="color_dark manrope-bold"
                >
                  Номер телефона
                </label>
                <input
                  className="input_atrib manrope-bold"
                  type="tel"
                  placeholder="Номер телефона (без +)"
                  value={registrationPhone}
                  onChange={(e) => setRegistrationPhone(normalizePhone(e.target.value))}
                  disabled={isLoading}
                />
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginTop: "10px",
                  }}
                >
                  <input
                    type="checkbox"
                    id="agreement-checkbox"
                    checked={agreementAccepted}
                    onChange={(e) => setAgreementAccepted(e.target.checked)}
                    style={{
                      width: "18px",
                      height: "18px",
                      cursor: "pointer",
                    }}
                  />
                  <label
                    htmlFor="agreement-checkbox"
                    style={{
                      fontSize: "14px",
                      color: "#282932",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                    className="manrope-bold"
                  >
                    Согласен с{" "}
                    <span
                      onClick={(e) => {
                        e.preventDefault();
                        setShowAgreementModal(true);
                      }}
                      style={{
                        color: "#3262FF",
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                    >
                      пользовательским соглашением
                    </span>
                  </label>
                </div>
                <button
                  className="btn_login manrope-bold"
                  onClick={handleSendCode}
                  disabled={isLoading || !agreementAccepted}
                  style={{
                    backgroundColor: isLoading || !agreementAccepted ? "#94A3B8" : "#3262FF",
                    cursor: isLoading || !agreementAccepted ? "not-allowed" : "pointer",
                  }}
                >
                  {isLoading ? "Отправка..." : "Отправить"}
                </button>
                <div
                  style={{
                    textAlign: "center",
                    marginTop: "10px",
                    cursor: "pointer",
                    color: "#94A3B8",
                    fontSize: "14px",
                  }}
                  className="manrope-bold"
                  onClick={handleCancelRegistration}
                >
                  Отмена
                </div>
              </>
            ) : (
              <>
                <label
                  style={{ textAlign: "left", width: "100%" }}
                  className="color_dark manrope-bold"
                >
                  Код подтверждения
                </label>
                <input
                  className="input_atrib manrope-bold"
                  type="text"
                  placeholder="Введите код"
                  value={registrationCode}
                  onChange={(e) => setRegistrationCode(e.target.value)}
                  disabled={isLoading}
                  maxLength={4}
                />
                <label
                  style={{ textAlign: "left", width: "100%" }}
                  className="color_dark manrope-bold"
                >
                  Имя / название
                </label>
                <input
                  className="input_atrib manrope-bold"
                  type="text"
                  placeholder="Имя или название компании"
                  value={registrationName}
                  onChange={(e) => setRegistrationName(e.target.value)}
                  disabled={isLoading}
                />
                <label
                  style={{ textAlign: "left", width: "100%" }}
                  className="color_dark manrope-bold"
                >
                  Пароль
                </label>
                <input
                  className="input_atrib manrope-bold"
                  type="password"
                  placeholder="Придумайте пароль"
                  value={registrationPassword}
                  onChange={(e) => setRegistrationPassword(e.target.value)}
                  disabled={isLoading}
                />
                <div
                  style={{
                    textAlign: "left",
                    width: "100%",
                    fontSize: "12px",
                    color: "#94A3B8",
                    marginTop: "-4px",
                    marginBottom: "8px",
                  }}
                  className="manrope-bold"
                >
                  {PASSWORD_HINT}
                </div>
                <button
                  className="btn_login manrope-bold"
                  onClick={handleVerifyCode}
                  disabled={isLoading}
                  style={{
                    backgroundColor: isLoading ? "#94A3B8" : "#3262FF",
                    cursor: isLoading ? "not-allowed" : "pointer",
                  }}
                >
                  {isLoading ? "Проверка..." : "ОК"}
                </button>
                <div
                  style={{
                    textAlign: "center",
                    marginTop: "10px",
                    cursor: "pointer",
                    color: "#94A3B8",
                    fontSize: "14px",
                  }}
                  className="manrope-bold"
                  onClick={handleCancelRegistration}
                >
                  Отмена
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Модальное окно с пользовательским соглашением */}
      {showAgreementModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setShowAgreementModal(false)}
        >
          <div
            style={{
              backgroundColor: "#FFFFFF",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "600px",
              maxHeight: "80vh",
              width: "90%",
              overflow: "auto",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: "20px",
                  color: "#282932",
                }}
                className="manrope-bold"
              >
                Пользовательское соглашение
              </h2>
              <button
                onClick={() => setShowAgreementModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#94A3B8",
                  padding: "0",
                  width: "30px",
                  height: "30px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ×
              </button>
            </div>
            <div
              style={{
                fontSize: "14px",
                color: "#282932",
                lineHeight: "1.6",
                whiteSpace: "pre-wrap",
              }}
              className="manrope-bold"
            >
              Настоящее согласие подтверждает, что в соответствии с требованиями ст. 9 Федерального закона РФ «О персональных данных», субъект персональных данных лично, свободно, в своём интересе и своей волей выражает согласие на автоматизированную и/или без использования средств автоматизации обработку персональных данных, указанных в настоящем согласии, а также на получение оператором персональных данных субъекта от третьих лиц.
Оператор обработки персональных данных: общество с ограниченной ответственностью «Регистратор», юридический адрес: 191124, город Санкт-Петербург, улица Новгородская, дом 23, литер А, помещение 141-Н №245; ОГРН 1167847076308; ИНН 7842088680; КПП 784201001 – далее – «оператор».
Персональные данные, подлежащие обработке: фамилия, имя, отчество, дата рождения, место рождения, пол, состояние в браке, паспортные данные, адрес места жительства и/или места временного проживания, дата регистрации по месту жительства, номера телефонов, иная контактная информация, идентификационный номер налогоплательщика, фотография, а также иные персональные данные, сообщённые субъектом персональных данных.
Цель обработки персональных данных: заключение, надлежащее исполнение, любые иные дополнительные действия сторон, в рамках заключенного субъектом и оператором гражданско-правового договора.
Действия с персональными данными, на которые даётся согласие: сбор, запись на любой вид носителей (бумажный, электронный и пр.), систематизация, накопление, хранение, уточнение (обновление, изменение), извлечение, использование, передача (распространение, предоставление, доступ), обезличивание, блокирование, удаление, уничтожение персональных данных.
Лица, которым может быть поручена обработка персональных данных: банки, нотариусы, архивные организации, государственные и муниципальные службы РФ, осуществляющие любые регистрационные действия, предусмотренные действующим законодательством РФ, в рамках заключенного субъектом и оператором гражданско-правового договора.
Срок: настоящее согласие действует бессрочно. Настоящее согласие, в любой момент, может быть отозвано субъектом персональных данных в порядке, установленном Федеральным законом РФ «О персональных данных» .
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
